import {
  PERSONA_VALUE_TIER_HIGH_MIN_PERCENT,
  PERSONA_VALUE_TIER_MEDIUM_MIN_PERCENT,
} from "../constants/personas.constants";
import type {
  Persona,
  PersonaSortDirection,
  PersonaSortKey,
  PersonaStatus,
  PersonaTableSortKey,
  PersonaType,
  PersonaValueTier,
} from "../types/personas.types";

/** Capitalize one hyphen/space segment without forcing the rest to lowercase (keeps acronyms). */
function capitalizeSegment(segment: string): string {
  if (!segment) return segment;
  return segment.charAt(0).toUpperCase() + segment.slice(1);
}

/**
 * BQ/sync titles often arrive lowercase (`the video-first brand builder`).
 * Display as title case, preserving hyphenated compounds (`Video-First`).
 */
export function humanizePersonaTitle(title: string): string {
  return title
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.split("-").map(capitalizeSegment).join("-"))
    .join(" ");
}

/** Sentence-case a pain/why line that arrives lowercase from BQ. */
export function humanizePersonaSentence(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return trimmed;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

/**
 * Industry keys from BQ often arrive as snake_case (`hair_care`) or a bare
 * lowercase slug (`undergarment`). Display as title case with spaces
 * (`Hair Care` / `Undergarment`). Already-cased labels pass through.
 */
export function humanizePersonaIndustry(industry: string): string {
  const trimmed = industry.trim();
  if (!trimmed) return trimmed;
  // Preserve intentional casing (e.g. "E-commerce", "Fashion styling").
  if (trimmed !== trimmed.toLowerCase()) return trimmed;
  return trimmed
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.split("-").map(capitalizeSegment).join("-"))
    .join(" ");
}

/** Competitor brand slugs (`true_classic`) → display names (`True Classic`). */
export function humanizePersonaCompetitor(competitor: string): string {
  return competitor
    .trim()
    .split(/[_-]+/)
    .filter(Boolean)
    .map(capitalizeSegment)
    .join(" ");
}

/** "The Scaling Ecom Founder" → "SF" — initials for the avatar placeholder (A2 real avatars deferred). */
export function personaInitials(title: string): string {
  const words = title.replace(/^The\s+/i, "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  const first = words[0]![0] ?? "";
  const last = words.length > 1 ? words[words.length - 1]![0] ?? "" : "";
  return `${first}${last}`.toUpperCase();
}

/**
 * `toLocaleDateString()` with no arguments follows the *browser* locale, so the
 * same row renders `15/08/2026` for one teammate and `8/15/2026` for another —
 * and neither reader can tell which half is the month. A worded month removes
 * the ambiguity outright.
 *
 * The year is dropped in the current year because personas arrive in onboarding
 * batches: every row shares a creation year, and repeating it down the column
 * costs four glyphs per row and carries no signal. `formatPersonaCreatedAtExact`
 * backs the compact form with the full timestamp on hover.
 */
export function formatPersonaCreatedAt(iso: string, locale: string, now: number = Date.now()): string {
  const created = new Date(iso);
  if (Number.isNaN(created.getTime())) return iso;
  const isCurrentYear = created.getFullYear() === new Date(now).getFullYear();
  return created.toLocaleDateString(locale, {
    day: "numeric",
    month: "short",
    ...(isCurrentYear ? {} : { year: "numeric" }),
  });
}

/** Full creation timestamp for the hover title, restoring what the compact form drops. */
export function formatPersonaCreatedAtExact(iso: string, locale: string): string {
  const created = new Date(iso);
  if (Number.isNaN(created.getTime())) return iso;
  return created.toLocaleString(locale, { dateStyle: "long", timeStyle: "short" });
}

/**
 * Compact spend for the library table. Thresholds match `formatMetricValue("currency")`
 * so KPI cards and the table never disagree on $3.8M vs $3,800,000 — the table just
 * paints the magnitude (`K`/`M`) quieter than the figure, because that's the column
 * you scan.
 */
export function formatPersonaSpendParts(
  spend: number,
  currencySymbol = "$",
): { value: string; suffix: "K" | "M" | null } {
  const v = spend ?? 0;
  if (Math.abs(v) >= 1_000_000) {
    return { value: `${currencySymbol}${(v / 1_000_000).toFixed(1)}`, suffix: "M" };
  }
  if (Math.abs(v) >= 1_000) {
    return { value: `${currencySymbol}${(v / 1_000).toFixed(1)}`, suffix: "K" };
  }
  return { value: `${currencySymbol}${v.toFixed(2)}`, suffix: null };
}

/** Exact dollars for the spend cell's hover title. */
export function formatPersonaSpendExact(spend: number, locale: string): string {
  return spend.toLocaleString(locale, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

/** Grouped integer — 824 stays 824, 2311 becomes 2,311. Never abbreviated. */
export function formatPersonaAdCount(count: number, locale: string): string {
  return Math.round(count).toLocaleString(locale);
}

/** Share of the tenant's total tracked spend this persona represents, 0–100. */
export function shareOfSpendPercent(spend: number, totalSpend: number): number {
  if (totalSpend <= 0) return 0;
  return Math.min(100, Math.max(0, (spend / totalSpend) * 100));
}

/** Coarse High/Medium/Low bucket over share-of-spend %, used by the value-tier filter (B4). */
export function personaValueTier(sharePct: number): PersonaValueTier {
  if (sharePct >= PERSONA_VALUE_TIER_HIGH_MIN_PERCENT) return "high";
  if (sharePct >= PERSONA_VALUE_TIER_MEDIUM_MIN_PERCENT) return "medium";
  return "low";
}

/** Case-insensitive match over title, Who, and pains — search scope per plan B4. */
export function personaMatchesSearch(persona: Persona, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [persona.title, persona.who ?? "", ...persona.pains].join(" ").toLowerCase();
  return haystack.includes(q);
}

/** Maps legacy toolbar dropdown keys onto table column keys. */
export function normalizePersonaSortKey(sortKey: PersonaSortKey): PersonaTableSortKey {
  if (sortKey === "mostUsed") return "adCount";
  if (sortKey === "recentlyAdded") return "createdAt";
  return sortKey;
}

const STATUS_SORT_RANK: Record<PersonaStatus, number> = {
  ACTIVE: 0,
  SUGGESTED: 1,
  REJECTED: 2,
};

const TYPE_SORT_RANK: Record<PersonaType, number> = {
  CORE_SUGGESTED: 0,
  INDUSTRY_SUGGESTED: 1,
  MANUAL: 2,
};

/** Comparators are ascending (a − b); `sortPersonas` applies direction. */
const PERSONA_SORT_COMPARATORS: Record<PersonaTableSortKey, (a: Persona, b: Persona) => number> = {
  title: (a, b) =>
    humanizePersonaTitle(a.title).localeCompare(humanizePersonaTitle(b.title), undefined, {
      sensitivity: "base",
    }),
  status: (a, b) => STATUS_SORT_RANK[a.status] - STATUS_SORT_RANK[b.status],
  createdAt: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  type: (a, b) => TYPE_SORT_RANK[a.type] - TYPE_SORT_RANK[b.type],
  shareOfSpend: (a, b) => a.spend - b.spend,
  spend: (a, b) => a.spend - b.spend,
  adCount: (a, b) => a.adCount - b.adCount,
};

/** Standard column-header toggle — re-click flips direction; a new column opens ascending. */
export function togglePersonaTableSort(
  currentKey: PersonaSortKey,
  currentDir: PersonaSortDirection,
  nextKey: PersonaTableSortKey,
): { sortKey: PersonaTableSortKey; sortDir: PersonaSortDirection } {
  const normalizedCurrent = normalizePersonaSortKey(currentKey);
  if (normalizedCurrent === nextKey) {
    return { sortKey: nextKey, sortDir: currentDir === "asc" ? "desc" : "asc" };
  }
  return { sortKey: nextKey, sortDir: "asc" };
}

/** Returns a new array — never mutates the query cache's `personas` array in place. */
export function sortPersonas(
  personas: Persona[],
  sortKey: PersonaSortKey,
  direction: PersonaSortDirection = "desc",
): Persona[] {
  const key = normalizePersonaSortKey(sortKey);
  const comparator = PERSONA_SORT_COMPARATORS[key];
  const multiplier = direction === "asc" ? 1 : -1;
  return [...personas].sort((a, b) => multiplier * comparator(a, b));
}
